import React, { useEffect } from 'react';
import { enqueueSnackbar } from '_shared/_providers/snackbars/snackbars';
import { ConfirmResponse, useConfirmDialog } from '_shared/_providers/dialogs/confirm/confirm-dialog';
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableRow from "@mui/material/TableRow";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";

import Guid from '_shared/guids/guid';
import { styled, Theme } from '@mui/material/styles';
import SystemNotification from 'notifications/system-notifications/system-notification';
import { SystemMessageSeverity } from 'notifications/system-notifications/system-message-severity';
import Stack from '@mui/material/Stack';
import PaginationParameters from '_shared/_api/queries/pagination/pagination-parameters';
import { HttpStatusCode } from 'axios';
import { getApiErrorUserMessage, readApiError } from '_shared/_errors/api-error';
import { getApiErrorStatus } from '_shared/_errors/api-error-message';
import {
  SuperUserAPIError,
  useCreateSystemNotificationMutation,
  useDeleteSystemNotificationMutation,
  useGetSystemNotificationsQuery,
  useUpdateSystemNotificationMutation,
} from 'users/super-users/_api/super-user-api';

const NotificationsPaper = styled(Paper)(({ theme }) => ({
  height: '100%',
  padding: theme.spacing(1),
  position: 'relative'
}));
const NotificationsContainer = styled(Box)(({ theme }) => ({
  [theme.breakpoints.down("xl")]: {
    gridTemplateColumns: "1fr",
    gridTemplateRows: "auto auto",
  },
  display: "grid",
  gridTemplateColumns: "1fr 2fr",
  gridTemplateRows: "1fr",
  width: "100%",
}));
const NotificationForm = styled(Stack)(({ theme }) => ({
  padding: theme.spacing(1),
}));
const NotificationFormTitle = styled(Typography)(({ theme }) => ({
  marginBottom: theme.spacing(1.5)
}));
const NotificationFormControl = styled(FormControl)(({ theme }) => ({
  marginBottom: theme.spacing(1.5),
  minWidth: '20rem'
}));
const NotificationInput = styled(TextField)(({ theme }) => ({
  marginBottom: theme.spacing(1.5),
  minWidth: '20rem'
}));
const NotificationSelect = styled(Select)(() => ({
  minWidth: '20rem'
}));
const SearchInput = styled(TextField)(({ theme }) => ({
  marginBottom: theme.spacing(1),
  maxWidth: '20rem',
}));
const NoRowsPlaceholder = styled(Typography)(({ theme }) => ({
  color: theme.palette.grey[600],
  fontSize: "1.3em",
}));
const getNotificationTypeMenuItemSx = (color: "info" | "warning" | "error") => (theme: Theme) => ({
  color: theme.palette[color].main,
});
const getNotificationTypeTextSx = (notificationType: SystemMessageSeverity | undefined) => {
  const color = notificationType ? getNotificationTypeColor(notificationType) : undefined;
  return color ? (theme: Theme) => ({ color: theme.palette[color].main }) : undefined;
};
function getNotificationTypeColor(notificationType: SystemMessageSeverity): "info" | "warning" | "error" | undefined {
  switch (notificationType) {
    case SystemMessageSeverity.Info:
      return "info";
    case SystemMessageSeverity.Warning:
      return "warning";
    case SystemMessageSeverity.Alert:
      return "error";
    default:
      return undefined;
  }
}
export const SYSTEM_NOTIFICATION_SEARCH_MAXIMUM_LENGTH = 200;
export const SYSTEM_NOTIFICATION_SEARCH_DELAY_MS = 300;
const ROWS_PER_PAGE_OPTIONS = [10, 25, 50, 100];

export const SYSTEM_NOTIFICATIONS_FETCH_FAILED = "Unable to fetch system notifications...";
export const SYSTEM_NOTIFICATIONS_REFUSED = "You do not have permission to view system notifications.";

function getNotificationTypeLabel(notificationType: SystemMessageSeverity | undefined): string {
  if (!notificationType) return "";
  return notificationType.charAt(0).toUpperCase() + notificationType.slice(1);
}

// A refusal is reported as a refusal, with the API's own reason when it sends one (#6490).
function getSystemNotificationsErrorMessage(error: unknown): string {
  const response = error instanceof SuperUserAPIError ? error.error : error;
  if (getApiErrorStatus(response) !== HttpStatusCode.Forbidden) return SYSTEM_NOTIFICATIONS_FETCH_FAILED;

  const refusal = readApiError(response);
  if (refusal.source !== "problem-details") return SYSTEM_NOTIFICATIONS_REFUSED;
  return getApiErrorUserMessage(refusal) ?? SYSTEM_NOTIFICATIONS_REFUSED;
}

export default function SystemNotifications() {
  const [notificationType, setNotificationType] = React.useState<SystemMessageSeverity | ''>('');
  const [notificationContent, setNotificationContent] = React.useState('');
  const [editingNotificationId, setEditingNotificationId] = React.useState<Guid | null>(null);
  const [searchText, setSearchText] = React.useState('');
  const [query, setQuery] = React.useState('');
  const [pagination, setPagination] = React.useState({
    pageIndex: PaginationParameters.DEFAULT_PAGE_INDEX,
    pageSize: PaginationParameters.DEFAULT_PAGE_SIZE,
  });
  // The same PaginationParameters on every render: RTK Query compares a class instance by reference,
  // so one built during render would send a failed read again on every render (#6488).
  const paginationParameters = React.useMemo(() => PaginationParameters.create(pagination), [pagination]);
  const notificationsQuery = useGetSystemNotificationsQuery({ pagination: paginationParameters, query });
  const [createSystemNotification, { isLoading: isCreating }] = useCreateSystemNotificationMutation();
  const [updateSystemNotification, { isLoading: isUpdating }] = useUpdateSystemNotificationMutation();
  const [deleteSystemNotification, { isLoading: isDeleting }] = useDeleteSystemNotificationMutation();

  const confirm = useConfirmDialog();
  const messageIsValid = notificationContent.trim() !== '';
  const formValid = notificationType !== '' && messageIsValid;
  const selectedNotificationTypeColor = notificationType ? getNotificationTypeColor(notificationType) : undefined;
  const systemNotifications: SystemNotification[] = notificationsQuery.data?.data ?? [];
  const totalNotifications = notificationsQuery.data?.headers.totalElements ?? 0;
  const saving = isCreating || isUpdating || isDeleting;

  // The search reaches the API once typing pauses, and always from the first page.
  useEffect(() => {
    const trimmed = searchText.trim();
    if (trimmed === query) return;
    const timeout = setTimeout(() => {
      setQuery(trimmed);
      setPagination((current) => (current.pageIndex === 0 ? current : { ...current, pageIndex: 0 }));
    }, SYSTEM_NOTIFICATION_SEARCH_DELAY_MS);
    return () => clearTimeout(timeout);
  }, [searchText, query]);

  useEffect(() => {
    if (!notificationsQuery.error) return;
    enqueueSnackbar(getSystemNotificationsErrorMessage(notificationsQuery.error), { variant: 'error' });
  }, [notificationsQuery.error]);

  async function saveNotification() {
    if (!notificationType || !messageIsValid) return;

    try {
      if (editingNotificationId) {
        await updateSystemNotification({
          notificationId: editingNotificationId,
          notificationType,
          notificationContent,
        }).unwrap();
      } else {
        await createSystemNotification({ notificationType, notificationContent }).unwrap();
      }
      clearForm();
    }
    catch {
      enqueueSnackbar(`Failed to ${editingNotificationId ? "update" : "create"} system notification`, { variant: 'error' });
    }
  }

  async function deleteNotification(notificationId: Guid) {
    const response = await confirm({
      title: "Delete system notification?",
      message: "Are you sure you want to delete this system notification?",
      okButtonText: "Yes",
      cancelButtonText: "No",
    });

    if (response === ConfirmResponse.Cancel) return;

    try {
      await deleteSystemNotification({ notificationId }).unwrap();
      // Deleting the only row on a later page would leave a page past the end.
      if (systemNotifications.length === 1 && pagination.pageIndex > 0) {
        setPagination((current) => ({ ...current, pageIndex: current.pageIndex - 1 }));
      }
    }
    catch {
      enqueueSnackbar("Failed to delete system notification", { variant: 'error' });
    }
  }

  function clearForm() {
    setNotificationType('');
    setNotificationContent('');
    setEditingNotificationId(null);
  }

  function editNotification(notification: SystemNotification) {
    setEditingNotificationId(notification.id);
    setNotificationType(notification.type ?? SystemMessageSeverity.Info);
    setNotificationContent(notification.message);
  }

  return (
    <NotificationsPaper>
      <NotificationsContainer>
        <NotificationForm>
          <NotificationFormTitle variant='h6'>
            {editingNotificationId ? 'Edit System Notification' : 'New System Notification'}
          </NotificationFormTitle>
          <NotificationFormControl required>
            <InputLabel id="notification-type-select-label">Notification Type</InputLabel>
            <NotificationSelect
              required
              labelId="notification-type-select-label"
              id="notification-type-select"
              value={notificationType}
              label="Notification Type"
              sx={
                selectedNotificationTypeColor
                  ? (theme) => ({ ".MuiSelect-select": { color: theme.palette[selectedNotificationTypeColor].main } })
                  : undefined
              }
              onChange={(event) => {
                setNotificationType(event.target.value as SystemMessageSeverity);
              }}>
              <MenuItem sx={getNotificationTypeMenuItemSx("info")} value={SystemMessageSeverity.Info}>Info</MenuItem>
              <MenuItem sx={getNotificationTypeMenuItemSx("warning")} value={SystemMessageSeverity.Warning}>Warning</MenuItem>
              <MenuItem sx={getNotificationTypeMenuItemSx("error")} value={SystemMessageSeverity.Alert}>Alert</MenuItem>
            </NotificationSelect>
          </NotificationFormControl>
          <NotificationInput
            required
            label="Message"
            minRows={5}
            multiline
            value={notificationContent}
            variant="outlined"
            error={notificationContent.length > 0 && !messageIsValid}
            helperText={notificationContent.length > 0 && !messageIsValid ? "Message cannot be empty." : undefined}
            onChange={(event) => {
              setNotificationContent(event.target.value);
            }}
          />
          <Button
            color="primary"
            variant="contained"
            disabled={saving || !formValid}
            onClick={saveNotification}>
            {editingNotificationId ? 'Save Changes' : 'Send Notification'}
          </Button>
          {editingNotificationId && (
            <Button color="inherit" onClick={clearForm} disabled={saving}>Cancel</Button>
          )}
        </NotificationForm>

        <NotificationForm>
          <NotificationFormTitle variant='h6'>
            Current System Notifications
          </NotificationFormTitle>
          <SearchInput
            label="Search system notifications"
            size="small"
            value={searchText}
            slotProps={{ htmlInput: { maxLength: SYSTEM_NOTIFICATION_SEARCH_MAXIMUM_LENGTH } }}
            onChange={(event) => {
              setSearchText(event.target.value);
            }}
          />
          {systemNotifications &&
            <Table aria-label="notification table">
              <TableHead>
                <TableRow>
                  <TableCell align="left">Type</TableCell>
                  <TableCell align="left">Message</TableCell>
                  <TableCell align="right">Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {systemNotifications.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={3} align="center">
                      <NoRowsPlaceholder>
                        {query ? 'No system notifications match your search' : 'No current system notifications'}
                      </NoRowsPlaceholder>
                    </TableCell>
                  </TableRow>
                ) : (
                  systemNotifications.map((row) => (
                    <TableRow key={row.id.value}>
                      <TableCell
                        align="left"
                        sx={getNotificationTypeTextSx(row.type)}
                      >
                        {getNotificationTypeLabel(row.type)}
                      </TableCell>
                      <TableCell align="left">{row.message}</TableCell>
                      <TableCell align='right'>
                        <Button
                          onClick={() => editNotification(row)}
                          color="primary">
                          Edit
                        </Button>
                        <Button
                          onClick={() => deleteNotification(row.id)}
                          color="primary">
                          Delete
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          }
          <TablePagination
            component="div"
            count={totalNotifications}
            page={pagination.pageIndex}
            rowsPerPage={pagination.pageSize}
            rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
            onPageChange={(_event, pageIndex) => {
              setPagination((current) => ({ ...current, pageIndex }));
            }}
            onRowsPerPageChange={(event) => {
              setPagination({ pageIndex: 0, pageSize: Number.parseInt(event.target.value, 10) });
            }}
          />
        </NotificationForm>
      </NotificationsContainer>
    </NotificationsPaper>
  )
}
